import { Card } from '../models/Card.js';
import { RANK_NAME, SUIT_SYM } from '../scoring/ScoringConstants.js';
import { TraditionalCard } from './TraditionalCard.js';

export class TableRenderer {
  static _animSeen = new Set();

  static takeAnim(key) {
    if (!key || TableRenderer._animSeen.has(key)) return false;
    TableRenderer._animSeen.add(key);
    return true;
  }

  static resetAnims() {
    TableRenderer._animSeen.clear();
  }

  static isWild(c) {
    if (c && typeof c.isWild === 'boolean') return c.isWild;
    return !!(c && c.wild);
  }

  static rankOf(c) {
    if (c && typeof c.rank === 'number') return c.rank;
    return (c && c.playAs && c.playAs.r) || (c ? c.r : 0);
  }

  static suitOf(c) {
    if (c && typeof c.suit === 'string') return c.suit;
    return (c && c.playAs && c.playAs.s) || (c ? c.s : 'S');
  }

  static faceMarkup(c) {
    const card = (c instanceof Card) ? c : Card.from(c);
    return TraditionalCard.renderHtml(card);
  }

  static cardEl(c, opts = {}, context = {}) {
    if (typeof document === 'undefined') return null;
    const card = (c instanceof Card) ? c : Card.from(c);
    const d = document.createElement('div');
    const shownS = TableRenderer.suitOf(card);
    const red = (card && typeof card.isRed === 'boolean') ? card.isRed : (!TableRenderer.isWild(card) && (shownS === 'H' || shownS === 'D'));
    const wildCard = TableRenderer.isWild(card);
    d.className = 'playing-card' + (red ? ' red' : '') + (wildCard ? ' wild' : '') + (wildCard && !card.playAs ? ' joker' : '');
    if (opts.playable) d.className += ' playable';
    if (opts.selected) d.className += ' selected';
    if (opts.disabled) d.className += ' disabled';
    if (opts.highlight) d.className += ' highlight-line';
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const animKey = opts.animKey || ((card && card.id) + ':' + opts.anim + ':' + (state && state.phase));
    const doFlip = opts.anim === 'flip' && TableRenderer.takeAnim(animKey);
    const isFaceDown = opts.back || Boolean(card && card.faceDown);
    if (opts.anim && opts.anim !== 'flip' && TableRenderer.takeAnim(animKey)) {
      d.classList.add('anim-' + opts.anim);
      if (opts.delay) d.style.animationDelay = opts.delay + 'ms';
    }
    if (doFlip && !isFaceDown) {
      d.classList.add('flipping');
      const inner = document.createElement('div');
      inner.className = 'card-3d';
      if (opts.delay) inner.style.animationDelay = opts.delay + 'ms';
      inner.innerHTML = `<div class="face back-face"></div><div class="face front">${TableRenderer.faceMarkup(card)}</div>`;
      d.appendChild(inner);
    } else if (!isFaceDown) {
      d.innerHTML = TableRenderer.faceMarkup(card);
    } else {
      d.className += ' back face-down';
      d.innerHTML = `<span></span><span class="suit">✦</span>`;
    }
    if (opts.badge && !isFaceDown) d.setAttribute('data-badge', opts.badge);
    if (opts.badgeBonus && !isFaceDown) d.classList.add('badge-bonus');
    if (card && card.id) d.setAttribute('data-cid', card.id);
    if (opts.onClick) d.addEventListener('click', opts.onClick);
    return d;
  }

  static cardKey(c) {
    if (!c) return '';
    if (c.id) return c.id;
    return TableRenderer.rankOf(c) + '-' + TableRenderer.suitOf(c);
  }

  static applyHighlights(cards, isBonus = false, context = {}) {
    if (typeof document === 'undefined') return;
    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    if (state) {
      state.highlightIds = (cards || []).map(TableRenderer.cardKey).filter(Boolean);
    }
    document.querySelectorAll('.playing-card.highlight-line, .playing-card.highlight-bonus').forEach(el => {
      el.classList.remove('highlight-line', 'highlight-bonus');
    });
    const ids = (state && state.highlightIds) || (cards || []).map(TableRenderer.cardKey).filter(Boolean);
    for (const id of ids) {
      const safe = String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      document.querySelectorAll('.playing-card[data-cid="' + safe + '"]').forEach(el => {
        el.classList.add(isBonus ? 'highlight-bonus' : 'highlight-line');
      });
    }
  }

  static cardLabel(c) {
    if (TableRenderer.isWild(c) && !c.playAs) return 'Joker';
    return (RANK_NAME[TableRenderer.rankOf(c)] || '★') + (SUIT_SYM[TableRenderer.suitOf(c)] || '');
  }

  static miniCardHtml(c, extra = '') {
    if (!c) return '';
    const s = TableRenderer.suitOf(c);
    const red = s === 'H' || s === 'D';
    return `<span class="mini-card${red ? ' red' : ''}${extra ? ' ' + extra : ''}">${TableRenderer.cardLabel(c)}</span>`;
  }
  /**
   * Main table layout render loop.
   * Updates role badges, player hand, opponent hands, crib, starter, played pile, and action controls.
   * @param {Object} context
   */
  static render(context = {}) {
    if (typeof document === 'undefined') {
      const r3d = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
      if (r3d && typeof r3d.syncFromGameState === 'function') {
        r3d.syncFromGameState({
          ...context,
          state: context.state || (typeof window !== 'undefined' ? window.state : null) || {},
          me: (context.mySeat ? context.mySeat() : (typeof window !== 'undefined' && typeof window.mySeat === 'function' ? window.mySeat() : 'p1'))
        });
      }
      return;
    }

    const state = context.state || (typeof window !== 'undefined' ? window.state : null) || {};
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const mySeatFn = context.mySeat || (typeof window !== 'undefined' ? window.mySeat : () => 'p1');
    const getActiveSeatIds = context.getActiveSeatIds || (typeof window !== 'undefined' ? window.getActiveSeatIds : () => ['p1', 'p2']);
    const getPlayerCount = context.getPlayerCount || (typeof window !== 'undefined' ? window.getPlayerCount : () => 2);
    const isSoloVsPairs = context.isSoloVsPairs || (typeof window !== 'undefined' ? window.isSoloVsPairs : () => false);
    const isOnline = context.isOnline || (typeof window !== 'undefined' ? window.isOnline : () => false);
    const isHvH = context.isHvH || (typeof window !== 'undefined' ? window.isHvH : () => false);
    const isSeatHuman = context.isSeatHuman || (typeof window !== 'undefined' ? window.isSeatHuman : () => true);
    const getSeatKept = context.getSeatKept || (typeof window !== 'undefined' ? window.getSeatKept : () => []);
    const getSeatHand = context.getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : () => []);
    const sortCards = context.sortCards || (typeof window !== 'undefined' ? window.sortCards : (cards) => cards);
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' ? window.cardLabel : (c) => (c ? `${c.r}${c.s}` : ''));
    const cardFits = context.cardFits || (typeof window !== 'undefined' ? window.cardFits : () => true);
    const cardEl = context.cardEl || (typeof window !== 'undefined' ? window.cardEl : null);
    const isWild = context.isWild || (typeof window !== 'undefined' ? window.isWild : () => false);
    const onPickCribCard = context.onPickCribCard || (typeof window !== 'undefined' ? window.onPickCribCard : () => {});
    const onYouCard = context.onYouCard || (typeof window !== 'undefined' ? window.onYouCard : () => {});
    const onCpuCard = context.onCpuCard || (typeof window !== 'undefined' ? window.onCpuCard : () => {});
    const onOppSeatDiscardCard = context.onOppSeatDiscardCard || (typeof window !== 'undefined' ? window.onOppSeatDiscardCard : () => {});
    const updateCoachDisplay = context.updateCoachDisplay || (typeof window !== 'undefined' ? window.updateCoachDisplay : () => {});
    const seatName = context.seatName || (typeof window !== 'undefined' ? window.seatName : (s) => s);
    const nextSeat = context.nextSeat || (typeof window !== 'undefined' ? window.nextSeat : (s) => (s === 'p1' ? 'p2' : 'p1'));
    const poneDoCutStarter = context.poneDoCutStarter || (typeof window !== 'undefined' ? window.poneDoCutStarter : () => {});
    const seatDiscardsToDeck = context.seatDiscardsToDeck || (typeof window !== 'undefined' ? window.seatDiscardsToDeck : () => false);
    const isDealerTeam = context.isDealerTeam || (typeof window !== 'undefined' ? window.isDealerTeam : () => false);
    const setShowMeter = context.setShowMeter || (typeof window !== 'undefined' ? window.setShowMeter : () => {});
    const updateTurnPrompt = context.updateTurnPrompt || (typeof window !== 'undefined' ? window.updateTurnPrompt : () => {});
    const renderScores = context.renderScores || (typeof window !== 'undefined' ? window.renderScores : () => {});
    const updateCountGlow = context.updateCountGlow || (typeof window !== 'undefined' ? window.updateCountGlow : () => {});
    const renderCutFan = context.renderCutFan || (typeof window !== 'undefined' ? window.renderCutFan : () => {});

    const count = Number(context.count || (settings && settings.playerCount) || (typeof getPlayerCount === 'function' ? getPlayerCount(settings) : 2)) || 2;
    const isMulti = count >= 3;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
    const discardN = isCanadianDoubles ? 2 : (isMulti ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2)));
    const me = mySeatFn();
    const oppSeats = getActiveSeatIds().filter(id => id !== me);
    const primaryOpp = oppSeats[0] || (me === 'p1' ? 'p2' : 'p1');

    // 1. Role Badges
    const isCutPhase = state.phase === 'cut' || state.phase === 'cut-reveal';
    const youRoleEl = document.getElementById('youRole');
    const cpuRoleEl = document.getElementById('cpuRole');

    if (isCutPhase) {
      const myCut = state.cutPicks && state.cutPicks[me];
      const oppCut = state.cutPicks && state.cutPicks[primaryOpp];
      if (state.phase === 'cut-reveal') {
        const isMeDeal = (state.dealer === me || state.dealer === 'you');
        if (youRoleEl) youRoleEl.textContent = myCut ? `(cut ${cardLabel(myCut)}${isMeDeal ? ' — deals!' : ''})` : '(cut)';
        if (cpuRoleEl) cpuRoleEl.textContent = oppCut ? `(cut ${cardLabel(oppCut)}${!isMeDeal ? ' — deals!' : ''})` : '(cut)';
      } else {
        if (youRoleEl) youRoleEl.textContent = myCut ? `(cut ${cardLabel(myCut)})` : '(cut)';
        if (cpuRoleEl) cpuRoleEl.textContent = oppCut ? `(cut ${cardLabel(oppCut)})` : '(cut)';
      }
    } else {
      const isMeDeal = (state.dealer === me || state.dealer === 'you');
      if (youRoleEl) {
        if (isSoloVsPairs()) {
          youRoleEl.textContent = isMeDeal ? '(dealer & crib)' : '(crib)';
        } else {
          youRoleEl.textContent = isMeDeal ? '(dealer)' : '(pone)';
        }
      }
      if (cpuRoleEl) {
        cpuRoleEl.textContent = (state.dealer === primaryOpp) ? '(dealer)' : '(pone)';
      }
    }

    const youTitleEl = document.getElementById('youTitle');
    if (youTitleEl) {
      const isMultiH = typeof context.isMultiHuman === 'function' ? context.isMultiHuman() : (typeof window !== 'undefined' && typeof window.isMultiHuman === 'function' ? window.isMultiHuman() : (settings && (settings.opp === 'online' || settings.isHvH)));
      const isOnlineFn = context.isSeatOnline || (typeof window !== 'undefined' ? window.isSeatOnline : null);
      const isOnline = typeof isOnlineFn === 'function' ? isOnlineFn(me) : true;
      const ind = isMultiH ? `<span class="online-indicator ${isOnline ? 'online' : 'offline'}" title="${isOnline ? 'Online' : 'Offline'}"></span>` : '';
      youTitleEl.innerHTML = `${ind}Your hand`;
    }

    // 2. Count Display
    const countValEl = document.getElementById('countVal');
    if (countValEl) countValEl.textContent = state.count != null ? state.count : 0;

    // 3. Cut Fan
    if (typeof renderCutFan === 'function') {
      renderCutFan();
    }

    // 4. Player Hand
    TableRenderer.renderPlayerHand({
      state, settings, me, isCutPhase, discardN,
      isOnline, isHvH, isSeatHuman, getSeatKept, getSeatHand,
      sortCards, cardEl, cardFits, isWild, onPickCribCard, onYouCard,
      updateCoachDisplay
    });

    // 5. Opponents Wrapper (2P or Multiplayer)
    TableRenderer.renderOpponents({
      state, settings, me, isMulti, oppSeats, primaryOpp, isCutPhase,
      isOnline, isHvH, isSeatHuman, isSeatOnline, isMultiHuman, getSeatKept, getSeatHand, sortCards, cardEl,
      cardLabel, cardFits, isWild, seatName, onPickCribCard, onCpuCard,
      onOppSeatDiscardCard
    });

    // 6. Crib Cards
    TableRenderer.renderCrib({
      state, settings, getPlayerCount, sortCards, cardEl
    });

    // 7. Starter Card
    TableRenderer.renderStarter({
      state, cardEl
    });

    // 8. Played Cards Pile
    TableRenderer.renderPlayedPile({
      state, cardEl, updateCountGlow
    });

    // 9. Action Buttons & Controls
    TableRenderer.renderActionControls({
      state, settings, me, discardN, isCutPhase, count,
      isOnline, isSeatHuman, seatName, nextSeat,
      poneDoCutStarter, seatDiscardsToDeck, isSoloVsPairs, isDealerTeam,
      setShowMeter, getSeatHand
    });

    // 10. Turn prompt and scores
    if (typeof updateTurnPrompt === 'function') updateTurnPrompt();
    if (typeof renderScores === 'function') renderScores(false);

    // 11. Synchronize 3D tabletop cards, seating avatars, and action HUD
    const renderer3D = context.boardRenderer3D || (typeof window !== 'undefined' ? (window.boardRenderer3D || window._currentRenderer3D) : null);
    if (renderer3D && typeof renderer3D.syncFromGameState === 'function') {
      renderer3D.syncFromGameState({
        state,
        settings,
        me,
        oppSeats,
        primaryOpp,
        getSeatHand,
        getSeatKept,
        sortCards,
        count,
        onYouCard,
        onPickCribCard,
        poneDoCutStarter,
        updateCoachDisplay
      });
    }
  }

  /**
   * Renders the local player's hand (#youCards).
   */
  static renderPlayerHand(ctx) {
    const youEl = document.getElementById('youCards');
    if (!youEl || typeof ctx.cardEl !== 'function') return;
    youEl.innerHTML = '';

    const {
      state, settings, me, isCutPhase, discardN,
      isOnline, isHvH, isSeatHuman, getSeatKept, getSeatHand,
      sortCards, cardEl, cardFits, isWild, onPickCribCard, onYouCard,
      updateCoachDisplay
    } = ctx;

    const isPassingDevice = !isOnline() && Boolean(state && state.passingTo);
    const isPickingMe = !isPassingDevice && state.phase === 'pick-crib' && state.currentPickPone === me;
    const activeHumanDiscard = !isOnline() ? (state.currentHumanDiscard || 'p1') : me;
    const isP1Turn = !isPassingDevice && activeHumanDiscard === 'p1' && (state.phase === 'discard' || state.phase === 'draw-1' || state.phase === 'draw-2');
    const hideYou = isPassingDevice || (!isOnline() && (
      (!isP1Turn && (state.phase === 'discard' || state.phase === 'discard-p2' || state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2')) ||
      (isHvH() && (state.phase === 'assign-p2' || (state.phase === 'play' && state.turn === 'p2'))) ||
      isPickingMe
    ));
    const canDiscard = !isPassingDevice && ((state.phase === 'discard' && (!isOnline() ? (activeHumanDiscard === 'p1') : (settings.netRole === 'host' || isSeatHuman(me)))) ||
                       (state.phase === 'discard-p2' && isOnline() && me === 'p2'));
    const isDrawMe = !isPassingDevice && (
      ((state.phase === 'draw-1' || state.phase === 'draw-2') && (!isOnline() ? (activeHumanDiscard === 'p1') : (settings.netRole === 'host' || isSeatHuman(me)))) ||
      ((state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2') && isOnline() && me === 'p2')
    );
    const selecting = canDiscard || isDrawMe;
    const assigning = !isPassingDevice && (state.phase === 'assign' && state.assignTarget === 'hand');
    const showHands = !isPassingDevice && (state.phase === 'show' || state.over);
    const myKept = getSeatKept(me);
    const myCutCard = (isCutPhase && state.cutPicks) ? state.cutPicks[me] : null;
    const myHand = myCutCard ? [myCutCard] : ((showHands && myKept && myKept.length) ? myKept : getSeatHand(me));
    const myTurn = !isPassingDevice && (state.turn === me || (me === 'p1' && state.turn === 'you'));

    const displayMe = isPickingMe ? myHand : sortCards(myHand);
    displayMe.forEach((c, i) => {
      const isCutCard = Boolean(isCutPhase && myCutCard && c.id === myCutCard.id);
      const selected = !isPassingDevice && (isPickingMe ? state.selected.includes(c.id) : (!hideYou && state.selected.includes(c.id)));
      const legalPlay = !isPassingDevice && state.phase === 'play' && myTurn && cardFits(c, state.count);
      const dealPhase = state.phase === 'discard' || state.phase === 'assign' || state.phase === 'play' || state.phase === 'cut-starter' || state.phase === 'starter-ritual' || state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2';

      const cardNode = cardEl(c, {
        back: isPassingDevice || (!isCutCard && hideYou && state.phase !== 'show' && !state.over) || isPickingMe,
        selected,
        highlight: !isPassingDevice && state.highlightIds && state.highlightIds.includes(c.id),
        playable: !isPassingDevice && (isPickingMe || (!isCutCard && !hideYou && (selecting || legalPlay || (assigning && isWild(c) && !c.playAs)))),
        disabled: isPassingDevice || (hideYou && !isPickingMe) || (state.phase === 'play' && myTurn && !legalPlay),
        onClick: isPassingDevice ? undefined : (isPickingMe ? () => onPickCribCard(me, c) : (hideYou ? undefined : () => onYouCard(c))),
        anim: isPassingDevice ? null : (isCutCard ? 'flip' : (state.phase === 'dealing' ? 'deal-you' : (dealPhase && state.phase !== 'show' ? 'deal' : null))),
        animKey: isCutCard ? ('cut-' + c.id) : ('hand-' + c.id + (state.phase === 'dealing' ? '-deal' : '')),
        delay: isCutCard ? 0 : (state.phase === 'dealing' ? i * 55 : (state.phase === 'cut-reveal' || state.phase === 'show' ? i * 90 : i * 70))
      });

      if (settings.coach === 'on' && state.phase === 'discard') {
        cardNode.addEventListener('mouseenter', () => {
          let sim = getSeatHand(me).filter(x => state.selected.includes(x.id));
          if (!state.selected.includes(c.id)) {
            sim = sim.concat([c]).slice(0, discardN);
          }
          if (sim.length === discardN) {
            updateCoachDisplay(sim);
          }
        });
        cardNode.addEventListener('mouseleave', () => {
          updateCoachDisplay();
        });
      }

      if (cardNode) {
        cardNode.style.zIndex = selected ? '12' : String(i + 1);
      }
      youEl.appendChild(cardNode);
    });
  }

  /**
   * Renders the opponents container (#opponentsWrapper) for 2P or multiplayer.
   */
  static renderOpponents(ctx) {
    const oppWrap = document.getElementById('opponentsWrapper');
    if (!oppWrap || typeof ctx.cardEl !== 'function') return;

    const {
      state, settings, me, isMulti, oppSeats, primaryOpp, isCutPhase,
      isOnline, isHvH, isSeatHuman, isSeatOnline, isMultiHuman, getSeatKept, getSeatHand, sortCards, cardEl,
      cardLabel, cardFits, isWild, seatName, onPickCribCard, onCpuCard,
      onOppSeatDiscardCard
    } = ctx;

    oppWrap.classList.toggle('multi', isMulti);

    const isPassingDevice = !isOnline() && Boolean(state && state.passingTo);
    const showHands = !isPassingDevice && (state.phase === 'show' || state.over);

    if (!isMulti) {
      if (!document.getElementById('cpuZone')) {
        oppWrap.innerHTML = `
          <div class="zone" id="cpuZone">
            <h3><span id="cpuTitle">${seatName(primaryOpp)}</span> <span id="cpuRole"></span></h3>
            <div class="cards" id="cpuCards"></div>
          </div>`;
      }
      const ct = document.getElementById('cpuTitle');
      if (ct) {
        const isMultiH = typeof isMultiHuman === 'function' ? isMultiHuman() : (typeof window !== 'undefined' && typeof window.isMultiHuman === 'function' ? window.isMultiHuman() : (settings && (settings.opp === 'online' || settings.opp === 'hvh' || settings.isHvH)));
        const oppIsHuman = typeof isSeatHuman === 'function' ? isSeatHuman(primaryOpp) : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman(primaryOpp) : true);
        const isOnlineFn = isSeatOnline || (typeof window !== 'undefined' ? window.isSeatOnline : null);
        const isOnline = typeof isOnlineFn === 'function' ? isOnlineFn(primaryOpp) : true;
        const ind = (isMultiH && oppIsHuman) ? `<span class="online-indicator ${isOnline ? 'online' : 'offline'}" title="${isOnline ? 'Online' : 'Offline'}"></span>` : '';
        const nameStr = isHvH() ? 'Player 2' : seatName(primaryOpp);
        ct.innerHTML = `${ind}${nameStr}`;
      }
      const cr = document.getElementById('cpuRole');
      const oppCut = state.cutPicks && state.cutPicks[primaryOpp];
      if (isCutPhase) {
        const isOppDeal = (state.dealer === primaryOpp);
        if (cr) {
          cr.textContent = (state.phase === 'cut-reveal' && oppCut)
            ? `(cut ${cardLabel(oppCut)}${isOppDeal ? ' — deals!' : ''})`
            : (oppCut ? `(cut ${cardLabel(oppCut)})` : '(cut)');
        }
      } else {
        if (cr) cr.textContent = (state.dealer === primaryOpp) ? '(dealer)' : '(pone)';
      }

      const cpuEl = document.getElementById('cpuCards');
      if (cpuEl) {
        cpuEl.innerHTML = '';
        const oppKept = getSeatKept(primaryOpp);
        const oppCutCard = (isCutPhase && state.cutPicks) ? state.cutPicks[primaryOpp] : null;
        const theirHand = oppCutCard ? [oppCutCard] : ((showHands && oppKept && oppKept.length) ? oppKept : getSeatHand(primaryOpp));
        const showAct = state.showAct;
        const showingOppHand = !isPassingDevice && state.phase === 'show' && (
          (showAct === 'pone' && (state.dealer === me)) ||
          (showAct === 'dealer' && (state.dealer === primaryOpp)) ||
          (showAct === primaryOpp) ||
          showAct === 'done'
        );
        const isDrawP2 = state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2' || ((state.phase === 'draw-1' || state.phase === 'draw-2') && state.currentHumanDiscard === 'p2');
        const p2Active = !isPassingDevice && isHvH() && (
          state.phase === 'discard-p2' ||
          (state.phase === 'discard' && state.currentHumanDiscard === 'p2') ||
          isDrawP2 ||
          state.phase === 'assign-p2' ||
          (state.phase === 'play' && state.turn === 'p2')
        );
        const isPickingOpp = !isPassingDevice && state.phase === 'pick-crib' && state.currentPickPone === primaryOpp;
        const face = !isPassingDevice && (Boolean(oppCutCard) || p2Active || state.over || state.phase === 'cut-reveal' || showingOppHand);
        const selectingP2 = !isPassingDevice && !isOnline() && (
          state.phase === 'discard-p2' ||
          (state.phase === 'discard' && state.currentHumanDiscard === 'p2') ||
          isDrawP2
        );

        const displayHand = isPickingOpp ? theirHand : sortCards(theirHand);
        displayHand.forEach((c, i) => {
          const isCutCard = Boolean(oppCutCard && c.id === oppCutCard.id);
          const el = cardEl(c, {
            back: !face,
            selected: !isPassingDevice && ((selectingP2 && state.selected.includes(c.id)) || (isPickingOpp && state.selected.includes(c.id))),
            highlight: !isPassingDevice && state.highlightIds && state.highlightIds.includes(c.id),
            playable: !isPassingDevice && ((p2Active && (selectingP2 || legalPlay || (state.phase === 'assign-p2' && isWild(c) && !c.playAs))) || isPickingOpp),
            disabled: isPassingDevice || (isHvH() && state.phase === 'play' && state.turn === 'p2' && !legalPlay),
            onClick: isPassingDevice ? undefined : (isPickingOpp ? () => onPickCribCard(primaryOpp, c) : (p2Active ? () => onCpuCard(c) : undefined)),
            anim: isPassingDevice ? null : (isCutCard ? 'flip' : (state.phase === 'dealing' ? 'deal-cpu' : (state.phase === 'show' ? null : 'deal'))),
            animKey: isCutCard ? ('cut-' + c.id) : ('opp-' + c.id + (state.phase === 'dealing' ? '-deal' : '')),
            delay: isCutCard ? 0 : (state.phase === 'dealing' ? i * 55 : (state.phase === 'cut-reveal' || state.phase === 'show' ? 80 + i * 90 : 60 + i * 70))
          });
          if (el) {
            el.style.zIndex = String(i + 1);
            cpuEl.appendChild(el);
          }
        });
      }
    } else {
      // 3 or more players
      oppWrap.innerHTML = oppSeats.map(sId => {
        const isDealer = state.dealer === sId;
        const oppCut = (isCutPhase && state.cutPicks) ? state.cutPicks[sId] : null;
        let roleText = isDealer ? '(dealer)' : '(pone)';
        if (state.phase === 'cut-reveal') {
          roleText = oppCut ? `(cut ${cardLabel(oppCut)}${isDealer ? ' — deals!' : ''})` : '(cut)';
        } else if (isCutPhase) {
          roleText = oppCut ? `(cut ${cardLabel(oppCut)})` : '(cut)';
        }
        const isMultiH = typeof isMultiHuman === 'function' ? isMultiHuman() : (typeof window !== 'undefined' && typeof window.isMultiHuman === 'function' ? window.isMultiHuman() : (settings && (settings.opp === 'online' || settings.opp === 'hvh' || settings.isHvH)));
        const oppIsHuman = typeof isSeatHuman === 'function' ? isSeatHuman(sId) : (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function' ? window.isSeatHuman(sId) : true);
        const isOnlineFn = isSeatOnline || (typeof window !== 'undefined' ? window.isSeatOnline : null);
        const isOnline = typeof isOnlineFn === 'function' ? isOnlineFn(sId) : true;
        const ind = (isMultiH && oppIsHuman) ? `<span class="online-indicator ${isOnline ? 'online' : 'offline'}" title="${isOnline ? 'Online' : 'Offline'}"></span>` : '';
        const isActiveTurn = state.phase === 'play' && state.turn === sId;
        return `
          <div class="opponent-seat-card ${isActiveTurn ? 'active-turn' : ''}" id="oppCard-${sId}">
            <div class="opp-seat-header">
              <span class="seat-tag ${sId}">${sId.toUpperCase()}</span>
              <span class="opp-name">${ind}${seatName(sId)}</span>
              <span class="opp-role-badge" id="${sId}Role">${roleText}</span>
            </div>
            <div class="cards opp-cards" id="${sId}Cards"></div>
          </div>`;
      }).join('');

      oppSeats.forEach(sId => {
        const oppCardsEl = document.getElementById(sId + 'Cards');
        if (!oppCardsEl) return;
        oppCardsEl.innerHTML = '';
        const kept = getSeatKept(sId);
        const oppCutCard = (isCutPhase && state.cutPicks) ? state.cutPicks[sId] : null;
        const hand = oppCutCard ? [oppCutCard] : ((showHands && kept && kept.length) ? kept : getSeatHand(sId));
        const isPickingThisPone = !isPassingDevice && state.phase === 'pick-crib' && state.currentPickPone === sId;
        const isShowingThisHand = !isPassingDevice && state.phase === 'show' && (state.showAct === sId || state.showAct === 'done');
        const isSeatDrawPhase = (state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2') && state.currentHumanDiscard === sId;
        const isSeatDiscarding = !isPassingDevice && !isOnline() && (state.phase === 'discard' || isSeatDrawPhase) && state.currentHumanDiscard === sId;
        const face = !isPassingDevice && (Boolean(oppCutCard) || state.over || isShowingThisHand || state.phase === 'cut-reveal' || isSeatDiscarding);

        const displayOppHand = (isPickingThisPone || isSeatDiscarding) ? hand : sortCards(hand);
        displayOppHand.forEach((c, i) => {
          const isCutCard = Boolean(oppCutCard && c.id === oppCutCard.id);
          const el = cardEl(c, {
            back: !face,
            selected: !isPassingDevice && ((isPickingThisPone || isSeatDiscarding) && state.selected.includes(c.id)),
            highlight: !isPassingDevice && state.highlightIds && state.highlightIds.includes(c.id),
            playable: !isPassingDevice && (isPickingThisPone || isSeatDiscarding),
            onClick: isPassingDevice ? undefined : (isPickingThisPone ? () => onPickCribCard(sId, c) : (isSeatDiscarding ? () => onOppSeatDiscardCard(sId, c) : undefined)),
            anim: isPassingDevice ? null : (isCutCard ? 'flip' : (state.phase === 'dealing' ? 'deal' : (face ? 'flip' : null))),
            animKey: isCutCard ? ('opp-cut-' + sId + '-' + c.id) : ('opp-' + sId + '-' + c.id),
            delay: isCutCard ? 0 : i * 40
          });
          if (el) {
            el.style.zIndex = String(i + 1);
            oppCardsEl.appendChild(el);
          }
        });
      });
    }
  }

  /**
   * Renders the crib zone (#cribCards).
   */
  static renderCrib(ctx) {
    const cribEl = document.getElementById('cribCards');
    if (!cribEl || typeof ctx.cardEl !== 'function') return;

    const { state, settings, getPlayerCount, sortCards, cardEl } = ctx;

    if (state && state.phase === 'play') {
      cribEl.classList.remove('counting');
      cribEl.classList.add('playing');
    } else if (state && (state.phase === 'show' || state.over)) {
      cribEl.classList.remove('playing');
      cribEl.classList.add('counting');
    } else if (cribEl) {
      cribEl.classList.remove('playing', 'counting');
    }

    cribEl.innerHTML = '';
    const cribCards = state.crib || [];

    sortCards(cribCards).forEach((c, i) => {
      const face = (settings.optStolenCrib && getPlayerCount() === 2)
        ? Boolean(state.stolenCribResolved && state.revealCrib)
        : (state.phase === 'show' || state.revealCrib);
      const el = cardEl(c, {
        back: !face,
        highlight: state.highlightIds && state.highlightIds.includes(c.id),
        anim: face ? 'flip' : 'pop',
        animKey: 'crib-' + c.id + (face ? '-up' : ''),
        delay: face ? i * 90 : i * 55
      });
      if (el) {
        el.style.zIndex = String(i + 1);
        cribEl.appendChild(el);
      }
    });
  }

  /**
   * Renders the starter zone (#starterZone).
   */
  static renderStarter(ctx) {
    const st = document.getElementById('starterZone');
    if (!st || typeof ctx.cardEl !== 'function') return;
    st.innerHTML = '';

    const { state, cardEl } = ctx;
    const starters = (state.starters && state.starters.length) ? state.starters : (state.starter ? [state.starter] : []);
    if (starters.length) {
      const ritualBack = state.phase === 'starter-ritual' && !state.starterFace;
      starters.forEach((stCard, idx) => {
        st.appendChild(cardEl(stCard, {
          back: ritualBack,
          highlight: !!(state.highlightIds && stCard.id && state.highlightIds.includes(stCard.id)),
          anim: ritualBack ? 'starter-slide' : 'flip',
          animKey: 'starter-' + stCard.id + (ritualBack ? '-slide' : '-flip'),
          delay: idx * 60
        }));
      });
    } else {
      st.innerHTML = '<div class="playing-card back"></div>';
    }
  }

  /**
   * Renders the played cards pile (#playedCards).
   */
  static renderPlayedPile(ctx) {
    const pl = document.getElementById('playedCards');
    if (!pl || typeof ctx.cardEl !== 'function') return;
    pl.innerHTML = '';

    const { state, cardEl, updateCountGlow } = ctx;
    if (state.phase === 'play' && !state.frozen) pl.classList.remove('sweeping', 'face-down');

    const pile = state.pile || [];
    const isFaceDown = Boolean(state && state.pileFaceDown);
    if (isFaceDown) pl.classList.add('face-down');

    pile.forEach((c, i) => {
      const el = cardEl(c, {
        back: isFaceDown,
        anim: isFaceDown ? 'flip' : 'play',
        animKey: 'pile-' + c.id + '-' + i + (isFaceDown ? '-down' : ''),
        delay: isFaceDown ? Math.min(i * 30, 150) : Math.min(i * 50, 250),
        badge: isFaceDown ? '' : (c.pegBadge || ''),
        badgeBonus: isFaceDown ? false : !!c.pegBadgeBonus
      });
      if (el) {
        if (isFaceDown) el.classList.add('back', 'face-down', 'flipping-down');
        el.style.zIndex = String(i + 1);
        pl.appendChild(el);
      }
    });

    if (typeof updateCountGlow === 'function') updateCountGlow();
  }

  /**
   * Renders action controls: deal deck, cut starter button, discard button, next button, and show meter.
   */
  static renderActionControls(ctx) {
    const {
      state, settings, me, discardN, isCutPhase,
      isOnline, isSeatHuman, seatName, nextSeat,
      poneDoCutStarter, seatDiscardsToDeck, isSoloVsPairs, isDealerTeam,
      setShowMeter, getSeatHand
    } = ctx;
    const count = Number(ctx.count || (settings && settings.playerCount) || (typeof window !== 'undefined' && typeof window.getPlayerCount === 'function' ? window.getPlayerCount(settings) : 2)) || 2;

    const isPassingDevice = !isOnline() && Boolean(state && state.passingTo);
    const isCutStarter = state.phase === 'cut-starter';

    // 1. Deal Deck
    const deckEl = document.getElementById('dealDeck');
    if (deckEl) {
      deckEl.classList.toggle('hidden', state.phase !== 'dealing' && state.phase !== 'starter-ritual' && !isCutStarter);
      const pone = state ? nextSeat(state.dealer) : null;
      const isMyCut = isCutStarter && (pone === me || (!isOnline() && isSeatHuman(pone)));
      const isGuess = Boolean(settings && settings.optGuessStarter);
      deckEl.classList.toggle('cuttable', isMyCut);
      if (isMyCut) {
        deckEl.title = isGuess ? 'Click to guess and cut the starter card' : 'Click to cut the starter card';
        deckEl.onclick = () => (typeof poneDoCutStarter === 'function' ? poneDoCutStarter() : null);
      } else {
        deckEl.title = 'Stock';
        deckEl.onclick = null;
      }
    }

    // 2. Cut Starter Button
    const btnCutStarter = document.getElementById('btnCutStarter');
    if (btnCutStarter) {
      const pone = state ? nextSeat(state.dealer) : null;
      const isMyCut = isCutStarter && (pone === me || (!isOnline() && isSeatHuman(pone)));
      btnCutStarter.classList.toggle('hidden', !isMyCut);
      if (isMyCut) {
        const isGuess = Boolean(settings && settings.optGuessStarter);
        btnCutStarter.textContent = (pone === 'p1' || pone === me)
          ? (isGuess ? '🎯 Guess & Cut Starter' : 'Cut Starter Card')
          : (isGuess ? `🎯 ${seatName(pone)}: Guess & Cut Starter` : `${seatName(pone)}: Cut Starter`);
        btnCutStarter.onclick = () => (typeof poneDoCutStarter === 'function' ? poneDoCutStarter() : null);
      }
    }

    // 3. Discard Button
    const mySId = me;
    const hasDiscarded = Boolean(state.seatDiscards && state.seatDiscards[mySId]);
    const canConfirmDiscard = !isPassingDevice && (isOnline()
      ? (!hasDiscarded && (state.phase === 'discard' || (state.phase === 'discard-p2' && mySId === 'p2')))
      : (state.phase === 'discard' || state.phase === 'discard-p2'));
    const isPickCribPhase = state.phase === 'pick-crib';
    const isDrawPhase = state.phase === 'draw-1' || state.phase === 'draw-2' || state.phase === 'draw-1-p2' || state.phase === 'draw-2-p2';
    const btnDiscard = document.getElementById('btnDiscard');

    if (btnDiscard) {
      if (isPickCribPhase) {
        const pickN = state.pickN || 2;
        const canConfirmPick = state.selected && state.selected.length === pickN;
        btnDiscard.classList.toggle('hidden', !canConfirmPick);
        btnDiscard.textContent = 'Take (' + pickN + ') to your crib';
      } else if (isDrawPhase) {
        const isFirstDraw = state.phase === 'draw-1' || state.phase === 'draw-1-p2';
        const selLen = (state.selected && state.selected.length) || 0;
        btnDiscard.classList.remove('hidden');
        if (selLen === 0) {
          btnDiscard.textContent = 'Keep Hand (Draw 0)';
        } else {
          btnDiscard.textContent = 'Discard & Draw (' + selLen + ')';
        }
      } else {
        const curSeat = state.currentHumanDiscard || 'p1';
        if (settings && settings.optCanadianDoubles && count === 4) {
          const handFn = getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : null);
          const curHand = typeof handFn === 'function' ? handFn(curSeat) : [];
          if (curHand && curHand.length === 10) {
            btnDiscard.classList.remove('hidden');
            btnDiscard.textContent = '🍁 Organize 10 Cards';
            btnDiscard.onclick = () => {
              const ModalCls = (typeof CC !== 'undefined' && CC.CanadianDoublesModal) || (typeof window !== 'undefined' ? (window.CanadianDoublesModal || window.CrazyCribbage?.CanadianDoublesModal) : null);
              if (ModalCls && typeof ModalCls.open === 'function') {
                const openCtx = Object.assign({}, ctx, {
                  showModal: ctx.showModal || (typeof window !== 'undefined' && window.crazyCribbageApp?.showModal ? window.crazyCribbageApp.showModal.bind(window.crazyCribbageApp) : (window.modalManager ? window.modalManager.showModal.bind(window.modalManager) : null)),
                  hideModal: ctx.hideModal || (typeof window !== 'undefined' && window.crazyCribbageApp?.hideModal ? window.crazyCribbageApp.hideModal.bind(window.crazyCribbageApp) : (window.modalManager ? window.modalManager.hideModal.bind(window.modalManager) : null)),
                  render: ctx.render || (typeof window !== 'undefined' && window.crazyCribbageApp?.render ? window.crazyCribbageApp.render.bind(window.crazyCribbageApp) : null)
                });
                ModalCls.open(curSeat, openCtx);
              }
            };
            return;
          }
        }
        const toDeck = seatDiscardsToDeck(curSeat);
        let targetDesc = 'the crib';
        const isCanadianDoubles = Boolean(settings && settings.optCanadianDoubles && count === 4);
        const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
        const isMulti = count >= 3;
        const resolvedDiscardN = ctx.discardN != null ? ctx.discardN : (isCanadianDoubles ? 2 : (isMulti ? 1 : (is7Card ? 2 : (settings && settings.deal === 'super' ? 3 : 2))));
        let currentDiscardN = resolvedDiscardN;
        if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
          const isDealer = curSeat === state.dealer;
          currentDiscardN = isDealer ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
          targetDesc = isDealer ? 'your crib' : "the dealer's hand";
        } else if (toDeck) {
          targetDesc = 'the deck';
        } else if (isSoloVsPairs()) {
          targetDesc = (curSeat === 'p1') ? 'your crib' : 'the crib';
        } else {
          targetDesc = isDealerTeam(curSeat) ? 'your crib' : "opponent's crib";
        }
        let hasRequiredFives = true;
        if (settings && settings.optFivesCribbage && !toDeck) {
          const handFn = getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : null);
          if (typeof handFn === 'function') {
            const hand = handFn(curSeat) || [];
            const fivesInHand = hand.filter(c => ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
            const reqFives = Math.min(fivesInHand.length, currentDiscardN);
            if (reqFives > 0) {
              const selIds = new Set(state.selected || []);
              const selectedFives = hand.filter(c => selIds.has(c.id) && ((c.rank !== undefined ? c.rank : c.r) || 0) === 5);
              if (selectedFives.length < reqFives) {
                hasRequiredFives = false;
              }
            }
          }
        }
        let hasRequiredSevensAndEights = true;
        if (settings && settings.optSevensAndEightsCribbage && !toDeck) {
          const handFn = getSeatHand || (typeof window !== 'undefined' ? window.getSeatHand : null);
          if (typeof handFn === 'function') {
            const hand = handFn(curSeat) || [];
            const targetInHand = hand.filter(c => {
              const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
              return r === 7 || r === 8;
            });
            const reqTarget = Math.min(targetInHand.length, currentDiscardN);
            if (reqTarget > 0) {
              const selIds = new Set(state.selected || []);
              const selectedTarget = hand.filter(c => {
                const r = ((c.rank !== undefined ? c.rank : c.r) || 0);
                return selIds.has(c.id) && (r === 7 || r === 8);
              });
              if (selectedTarget.length < reqTarget) {
                hasRequiredSevensAndEights = false;
              }
            }
          }
        }
        const selLen = (state.selected && state.selected.length) || 0;
        btnDiscard.classList.toggle('hidden', !(canConfirmDiscard && selLen === currentDiscardN));
        if (selLen === currentDiscardN) {
          if (!hasRequiredFives) {
            btnDiscard.textContent = 'Must discard 5s to ' + targetDesc;
          } else if (!hasRequiredSevensAndEights) {
            btnDiscard.textContent = 'Must discard 7s & 8s to ' + targetDesc;
          } else {
            btnDiscard.textContent = 'Discard (' + currentDiscardN + ') to ' + targetDesc;
          }
        } else {
          btnDiscard.textContent = 'Discard selected';
        }
      }
    }

    // 4. Next Button
    const btnNext = document.getElementById('btnNext');
    if (btnNext) {
      btnNext.classList.toggle('hidden', state.phase !== 'await');
    }

    // 5. Show Meter
    if (typeof setShowMeter === 'function') {
      if (state.showMeter) {
        setShowMeter(state.showMeter.act, state.showMeter.total, state.showMeter.line);
      } else if (state.phase !== 'show') {
        setShowMeter('', 0, '');
      }
    }
  }
}
